// Builder puro del payload para POST /actuacion desde el wizard nuevo.
// Convive con el legacy `prepareDoc(state)` en src/store/modules/sumario/formatPost.js
// hasta que se valide en producción. No tocar el legacy mientras dure la coexistencia.
//
// Contrato con el backend (Controllers_v2_actuaciones.php + traits/actuacion_validate.php):
//   {
//     Denuncia, Sumario,
//     Denunciante: [[{Rol,Descripcion},{...persona},{Orden}], ...],
//     Acusado:     [[{Rol,Descripcion},{...persona},{Orden}], ...],
//     MomentoUbicacion: [{...}],
//     ModusOperandi:    {Descripcion, Nombre},
//     Juzgadointerviniente: {Descripcion, Nombre, JuzgadoID},
//     Efectos: [{...}],
//     AfectacionPatrimonial: [{...}],
//     ActuacionArticulo: { Articulos: [...] },
//     Sumariocausacartula: { Caratulas: [{TipocausacaratulaID,Nombre,TipoCaratula}] },
//     ActuacionUfi: { TipoufiID, Numero },
//     PersonalInterviniente: { DatosPersonalInterviniente: [...] },
//     PersonalFiscal: [...],
//     Sitio: { TipoSitioID } | null,
//     State: {}   // vacío por decisión del equipo (se conservaba el state de Vuex antes)
//   }

import { getTiposumarioID, isExpediente, isUfi } from "./typeToTiposumario";

const toStr = (v) => (v === undefined || v === null ? "" : String(v));

const formatBackendDate = (value) => {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return toStr(value);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const nowBackendDate = () => formatBackendDate(new Date());

// Accesos seguros a catálogos: aceptamos tanto `Pinia arrays` como `store.state.siis`.
const findById = (list, idKey, id) => {
  if (!Array.isArray(list) || id === undefined || id === null || id === "") return null;
  const target = String(id);
  return list.find((x) => String(x?.[idKey]) === target) || null;
};

// Algunos steps guardan la lista con object-spread reactivo (`{0: x, 1: y, length: 2}`)
// en lugar de un array plano. Aceptamos ambos y filtramos huecos.
const sanitizeList = (raw) => {
  if (raw === null || raw === undefined) return [];
  let arr;
  if (Array.isArray(raw)) {
    arr = Array.from(raw);
  } else if (typeof raw === "object") {
    arr = Object.values(raw);
  } else {
    return [];
  }
  return arr.filter((x) => x && typeof x === "object");
};

// Strip HTML tags para extraer texto plano del acta cuando no hay `text` propio.
const htmlToText = (html) => {
  if (!html || typeof html !== "string") return "";
  return html
    .replace(/<\/(p|div|h[1-6]|li|br)>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

// El editor arranca con solo `actaDoc.json` poblado (ProseMirror/TipTap);
// html y text quedan vacíos hasta que el usuario interactúa. Extraemos el texto
// recorriendo la estructura.
const tiptapJsonToText = (node) => {
  if (!node || typeof node !== "object") return "";
  if (typeof node.text === "string") return node.text;
  const children = Array.isArray(node.content) ? node.content : [];
  const parts = children.map(tiptapJsonToText).filter(Boolean);
  const blockTypes = [
    "paragraph",
    "heading",
    "listItem",
    "blockquote",
    "bulletList",
    "orderedList",
    "doc",
  ];
  const joiner = blockTypes.includes(node.type) ? "\n" : "";
  return parts.join(joiner);
};

// Normaliza flags booleanos que el backend PHP evalúa con `if (...)`.
// "false" string en PHP es truthy, por eso lo convertimos a boolean real.
const toPhpBool = (v) => {
  if (v === true || v === 1 || v === "1" || v === "true") return true;
  return false;
};

// Para Model_v3_PersonaFisica: `is_natural|less_than_equal_to[1]`.
// Algunas stores envían `String(undefined)` → "undefined" y rompen la validación.
const toNatBit = (v, def = 1) => {
  if (v === true || v === 1 || v === "1") return 1;
  if (v === false || v === 0 || v === "0") return 0;
  const n = Number(v);
  return Number.isFinite(n) && (n === 0 || n === 1) ? n : def;
};

// Devuelve la representación string de un valor que puede ser string, número u
// objeto con { nombre } | { Nombre } | { label }. Evita "[object Object]".
const nameOf = (v) => {
  if (v === null || v === undefined) return "";
  if (typeof v === "string" || typeof v === "number") return String(v);
  if (typeof v === "object") {
    return toStr(v.nombre ?? v.Nombre ?? v.label ?? v.value ?? "");
  }
  return "";
};

// ------------ Sub-builders ------------

// Rol: el wizard usa TipoRolPersonaFisicaID (objeto {id, nombre} o string ya normalizado).
const rolFromItem = (item) => {
  const r = item?.TipoRolPersonaFisicaID;
  if (!r) return "";
  if (typeof r === "string") return r;
  return r?.nombre || "";
};

const personaFromItem = (item) => ({
  Apellido: toStr(item?.Apellido),
  Nombre: toStr(item?.Nombre),
  Telefono: toStr(item?.Telefono),
  CorreoElectronico: toStr(item?.CorreoElectronico ?? item?.Email),
  NumeroIdentificacion: toStr(item?.NumeroIdentificacion),
  TipoDocumentoID: toStr(item?.TipoDocumentoID),
  Nacionalidad: toStr(item?.Nacionalidad),
  Sexo: toStr(item?.Sexo),
  EstadoCivil: toStr(item?.EstadoCivil),
  FechaDeNacimiento: toStr(item?.FechaDeNacimiento).slice(0, 10),
  ExhibeDocumento: toNatBit(item?.ExhibeDocumento, 1),
  TieneEstudios: toNatBit(item?.TieneEstudios, 1),
  Instruccion: toStr(item?.Instruccion),
  Profesion: toStr(item?.Profesion),
  DomicilioSipe: toStr(item?.DomicilioSipe),
  Observaciones: toStr(item?.Observaciones),
  PersonaJuridica: toPhpBool(item?.PersonaJuridica),
  Cuit: toStr(item?.Cuit),
  Denominacion: toStr(item?.Denominacion),
  DomicilioJuridico: toStr(item?.DomicilioJuridico),
  Rubro: toStr(item?.Rubro),
  TelefonoJuridico: toStr(item?.TelefonoJuridico),
  TieneSeguro: toPhpBool(item?.TieneSeguro),
  Identikit: item?.Identikit ?? [],
  codigoCoelsa: toStr(item?.coelsa ?? item?.codigoCoelsa),
});

const buildPersonasTuples = (rawList, tipoKey) => {
  const list = sanitizeList(rawList);
  if (list.length === 0) return [];
  return list.map((item, idx) => {
    const rol = rolFromItem(item);
    return [
      {
        Rol: rol,
        Descripcion: rol ? `${rol} de la actuacion` : `Persona de la actuacion (${tipoKey})`,
      },
      personaFromItem(item),
      { Orden: idx },
    ];
  });
};

const firstCaratulaName = (wizard, catalogs) => {
  const ids = wizard?.datosLegales?.data?.causasCaratulas || [];
  const first = Array.isArray(ids) ? ids[0] : null;
  if (!first) return "";
  if (typeof first === "object") return first?.Nombre || "";
  const cat = findById(catalogs?.tiposCausaCaratula, "TipocausacaratulaID", first);
  return cat?.Nombre || "";
};

const buildDenuncia = (wizard, tipoKey, catalogs) => {
  const explicitRelato = typeof wizard?.relato === "string" ? wizard.relato.trim() : "";
  let relato = explicitRelato;
  if (!relato) {
    const acta = wizard?.actaDoc || {};
    const textFromNode = typeof acta.text === "string" && acta.text.trim() ? acta.text : "";
    relato =
      textFromNode ||
      htmlToText(acta.html) ||
      tiptapJsonToText(typeof acta.json === "object" ? acta.json : null) ||
      (typeof acta.json === "string" ? acta.json : "") ||
      "";
  }

  return {
    Fechacreacion: nowBackendDate(),
    Fechaenvio: "",
    Nombre: firstCaratulaName(wizard, catalogs),
    Relato: relato,
    // Backend legacy envía el string completo del tipo (igual a TiposumarioID).
    // Las validaciones del UFI y de Afectación Patrimonial hacen strpos / comparación
    // exacta contra el nombre completo del tipo.
    TipodenunciaID: getTiposumarioID(tipoKey),
  };
};

const buildSumario = (wizard, tipoKey, catalogs) => {
  const legal = wizard?.datosLegales?.data || {};
  const legajo = toStr(legal.legajoNumero).trim();
  const anio = toStr(legal.anio).trim();
  const numero = legajo && anio ? `${legajo}/${anio}` : legajo || "";

  return {
    Fechafin: "",
    Numero: numero,
    TiposumarioID: getTiposumarioID(tipoKey),
    FechaCreacion: nowBackendDate(),
    CausaCaratula: firstCaratulaName(wizard, catalogs),
  };
};

const buildMomentoUbicacion = (wizard) => {
  const pre = wizard?.fecha?.data?.preUbicacion || {};
  const mom = wizard?.fecha?.data?.preMoment || {};
  return [
    {
      MomentoFechaHoraDesde: formatBackendDate(mom.date),
      MomentoFechaHoraHasta: formatBackendDate(mom.date2),
      Calle: toStr(pre.calle),
      Numero: toStr(pre.numero),
      Departamento: nameOf(pre.departamento),
      Casa: toStr(pre.casa),
      Orientacion: nameOf(pre.orientacion),
      Sector: toStr(pre.sector),
      Piso: toStr(pre.piso),
      Barrio: toStr(pre.barrio),
      Zona: toStr(pre.zona),
      Longitud: toStr(pre.coordinates?.lng),
      Latitud: toStr(pre.coordinates?.lat),
    },
  ];
};

const buildModusOperandi = (wizard, catalogs) => {
  const id = wizard?.datosLegales?.data?.modusOperandiId;
  const modus = findById(catalogs?.modusOperandi, "TipomodusoperandiID", id);
  const nombre = modus?.Nombre || "";
  const legal = wizard?.datosLegales?.data || {};
  const legajo = toStr(legal.legajoNumero).trim();
  const anio = toStr(legal.anio).trim();
  const numero = legajo && anio ? `${legajo}/${anio}` : legajo || "";
  // El backend valida Descripcion no vacío; replicamos el patrón del legacy.
  return {
    Descripcion: numero ? `Modus operandi del sumario N° ${numero}` : "Modus operandi del sumario",
    Nombre: nombre,
  };
};

const buildJuzgado = (wizard, catalogs) => {
  const id = wizard?.datosLegales?.data?.juzgadoId;
  const juz = findById(catalogs?.juzgados, "JuzgadoID", id);
  return {
    Descripcion: "",
    Nombre: juz?.Nombre || "",
    JuzgadoID: toStr(id),
  };
};

const buildEfectos = (wizard) => {
  const list = wizard?.efectos?.data?.effectList || [];
  return list
    .filter((e) => e && (e.CategoriaID || e.MarcaID || e.ModeloID))
    .map((e) => ({
      CategoriaID: toStr(e.CategoriaID),
      SubcategoriaID: toStr(e.SubcategoriaID),
      TipoID: toStr(e.TipoID),
      MarcaID: toStr(e.MarcaID),
      ModeloID: toStr(e.ModeloID),
      Anio: toStr(e.Anio),
      Chasis: toStr(e.Chasis),
      NumeroMotor: toStr(e.NumeroMotor),
      Dominio: toStr(e.Dominio),
      NumeroSerie: toStr(e.NumeroSerie),
      Imei: toStr(e.Imei),
      Abonado: toStr(e.Abonado),
      Color: toStr(e.Color),
      tipoEfecto: toStr(e.Tipoefecto ?? e.tipoEfecto),
      EstadoEfectoActuacionID: toStr(e.EstadoEfectoActuacionID ?? e.Tipoefecto),
    }));
};

const buildAfectacionPatrimonial = (wizard) => {
  const list = wizard?.afectaciones?.data?.afectacionesPatrimoniales;
  const asArr = Array.isArray(list) ? list : Object.values(list || {});
  return asArr
    .filter((a) => a && (a.TipoAfectacionPatrimonial || a.Monto))
    .map((a) => ({
      fecha: toStr(a.FechaCreacion),
      monto: toStr(a.Monto),
      // El backend lee $tipoMoneda['value'] para obtener TipoMonedaID.
      tipoMoneda: { value: toStr(a.Moneda?.id ?? ""), nombre: toStr(a.Moneda?.nombre ?? "") },
      identificadorOrigen: toStr(a.IdentificadorFinancieroOrigen) || "-",
      identificadorDestino: toStr(a.IdentificadorFinancieroDestino),
      afectadoId: a.Afectado?.value ?? a.Afectado ?? null,
      vinculadoId: a.Vinculado?.value ?? a.Vinculado ?? null,
      esPropietario: a.PropietarioCuenta?.id ?? a.PropietarioCuenta ?? null,
      observaciones: toStr(a.Descripcion) || "-",
      NroTarjeta: a.NumeroTarjeta || null,
      entidadBancaria: a.EntidadBancaria || "-",
      bienEntregado: a.BienEntregadoID || null,
      tipo: toStr(a.TipoAfectacionPatrimonial?.value ?? a.TipoAfectacionPatrimonial ?? ""),
      Dni: toStr(a.PropietarioDNI),
      NombreCompleto: toStr(a.PropietarioNombre),
    }));
};

const buildArticulos = (wizard, catalogs) => {
  const ids = wizard?.datosLegales?.data?.articulos || [];
  const catalog = Array.isArray(catalogs?.articulos) ? catalogs.articulos : [];
  const list = ids
    .map((id) => findById(catalog, "ArticulosID", id))
    .filter(Boolean)
    .map((a) => ({
      ArticulosID: toStr(a.ArticulosID),
      Codigo: toStr(a.Codigo),
      Numeroarticulo: toStr(a.Numeroarticulo),
    }));
  return { Articulos: list };
};

const buildCaratulas = (wizard, catalogs) => {
  const ids = wizard?.datosLegales?.data?.causasCaratulas || [];
  const list = ids
    .map((id) => findById(catalogs?.tiposCausaCaratula, "TipocausacaratulaID", id))
    .filter(Boolean)
    .map((c) => ({
      TipocausacaratulaID: c.TipocausacaratulaID,
      Nombre: c.Nombre || "",
      TipoCaratula: c.TipoCaratula || "",
    }));
  return { Caratulas: list };
};

const buildActuacionUfi = (wizard, catalogs) => {
  const id = wizard?.datosLegales?.data?.ufiId;
  if (!id) return { TipoufiID: "", Numero: "" };
  const ufi = findById(catalogs?.ufis, "TipoufiID", id);
  return {
    TipoufiID: toStr(id),
    Numero: ufi?.Numero || "",
  };
};

const buildPersonalInterviniente = (wizard, catalogs) => {
  const raw = wizard?.intervinientes?.data?.intervinientesList;
  const list = Array.isArray(raw) ? raw : Object.values(raw || {});
  const jerarquias = Array.isArray(catalogs?.jerarquias) ? catalogs.jerarquias : [];
  const resolveJerarquiaId = (item) => {
    const direct = item?.JerarquiaID ?? item?.jerarquia?.id;
    if (direct !== undefined && direct !== null && direct !== "") return toStr(direct);
    const nombre = toStr(item?.Jerarquia || item?.jerarquia?.nombre);
    if (!nombre) return "";
    const match = jerarquias.find((j) => toStr(j?.nombre) === nombre);
    return match ? toStr(match.id) : "";
  };
  const datos = list
    .filter((i) => i && i.Nombreyapellido)
    .map((i) => ({
      nombre: toStr(i.Nombreyapellido),
      dependencia: toStr(i.Dependencia),
      jerarquia: {
        id: resolveJerarquiaId(i),
        nombre: toStr(i.Jerarquia),
      },
    }));
  return { DatosPersonalInterviniente: datos };
};

const buildPersonalFiscal = (wizard) => {
  const fiscalId = wizard?.datosLegales?.data?.fiscalId;
  const ayudanteId = wizard?.datosLegales?.data?.ayudanteId;
  if (!fiscalId && !ayudanteId) return [];
  const arr = [];
  if (fiscalId) arr.push({ PersonalfiscalID: fiscalId, Jerarquia: "Fiscal" });
  if (ayudanteId) arr.push({ PersonalfiscalID: ayudanteId, Jerarquia: "Ayudante" });
  return arr;
};

const buildSitio = (wizard) => {
  const id = wizard?.datosLegales?.data?.sitioId;
  return id ? { TipoSitioID: toStr(id) } : null;
};

// ------------ Entry point ------------

export const buildActuacionPayload = (wizard, { catalogs = {}, user = null } = {}) => {
  const tipoKey = wizard?.type;
  if (!tipoKey) throw new Error("wizard.type no definido; no se puede construir el payload");

  const tipoBackend = getTiposumarioID(tipoKey);
  if (!tipoBackend) throw new Error(`No hay mapeo de TiposumarioID para "${tipoKey}"`);

  const payload = {
    Denuncia: buildDenuncia(wizard, tipoKey, catalogs),
    Sumario: buildSumario(wizard, tipoKey, catalogs),
    Denunciante: buildPersonasTuples(wizard?.afectados?.data?.affectedList, "afectados"),
    Acusado: buildPersonasTuples(wizard?.vinculados?.data?.vinculatedList, "vinculados"),
    MomentoUbicacion: buildMomentoUbicacion(wizard),
    ModusOperandi: buildModusOperandi(wizard, catalogs),
    Juzgadointerviniente: buildJuzgado(wizard, catalogs),
    Efectos: buildEfectos(wizard),
    AfectacionPatrimonial: buildAfectacionPatrimonial(wizard),
    ActuacionArticulo: buildArticulos(wizard, catalogs),
    Sumariocausacartula: buildCaratulas(wizard, catalogs),
    ActuacionUfi: buildActuacionUfi(wizard, catalogs),
    PersonalInterviniente: buildPersonalInterviniente(wizard, catalogs),
    PersonalFiscal: buildPersonalFiscal(wizard),
    Sitio: buildSitio(wizard),
    State: {},
  };

  // Expedientes no llevan carátulas, llevan artículos; la UFI lleva ambos campos pero
  // el backend no valida ActuacionUfi salvo en UFIs.
  if (isExpediente(tipoBackend)) payload.Sumariocausacartula = { Caratulas: [] };
  if (!isUfi(tipoBackend)) payload.ActuacionUfi = { TipoufiID: "", Numero: "" };

  // user se mantiene como hook por si el backend mañana necesitara algo extra.
  void user;

  return payload;
};

export default buildActuacionPayload;
